import {
  caluclateCHPercentage,
  caluclatePL,
  calculateCh,
  calculateSize,
  calcualteDeltaAdjustedShareCount,
} from "../../../component/Methods/MonitorScreenFormulae.js";

import { useSelector } from "react-redux";
import { findName, calculateTdg } from "../../../component/Methods/index.js";
import { formatTimeAgo } from "../../../utils/helpers.js";
import { fetchDeltasForTickers } from "../../../utils/polygon.js";

import {
  findLivePriceForTicker,
  getLivePriceTickerKey,
  normalizeLivePriceEntry,
} from "../../../utils/livePriceTickerMatch";
import { isIgnoredMonitorStrategy } from "../../../utils/ignoredMonitorStrategies";

const contractUnits = 100;
let fetched = "N/A";

// Helper function to safely call toFixed on potentially undefined values
function safeToFixed(value, decimals = 2) {
  if (value === undefined || value === null || isNaN(value)) {
    return "0." + "0".repeat(decimals);
  }
  return Number(value).toFixed(decimals);
}

// Helper function to safely parse float values
function safeParseFloat(value, defaultValue = 0) {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

export function computeNavFromRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return 0;
  }

  return rows.reduce((sum, row) => {
    const quantity = safeParseFloat(row?.deltaAdjustedShareCount);
    const marketPrice = safeParseFloat(row?.sharePrice);
    const avgCost = safeParseFloat(row?.avgCost);

    if (quantity > 0) {
      return sum + quantity * marketPrice;
    }

    if (quantity < 0) {
      const shortShares = Math.abs(quantity);
      const shortAssets = shortShares * avgCost;
      const shortLiability = shortShares * marketPrice;
      return sum + shortAssets - shortLiability;
    }

    return sum;
  }, 0);
}

/**
 * BoD / SOD capital mark: pre-trade qty + avgCost, priced at yesterday's close.
 * Long: SOD shares × yest. Short unrealized: |SOD| × (avgCost − yest).
 */
export function computeSodNavFromRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return 0;
  }

  return rows.reduce((sum, row) => {
    const quantity =
      row?.deltaAdjustedShareCountWithoutTrades !== undefined
        ? safeParseFloat(row.deltaAdjustedShareCountWithoutTrades)
        : safeParseFloat(row?.deltaAdjustedShareCount);
    // BoD mark = yest close (not live). Fall back to live only if yest missing.
    const yestPrice = safeParseFloat(row?.last);
    const bodPrice =
      yestPrice !== 0 ? yestPrice : safeParseFloat(row?.sharePrice);
    const avgCost =
      row?.avgCostWithoutTrades !== undefined
        ? safeParseFloat(row.avgCostWithoutTrades)
        : safeParseFloat(row?.avgCost);

    if (quantity > 0) {
      return sum + quantity * bodPrice;
    }

    if (quantity < 0) {
      const shortShares = Math.abs(quantity);
      const shortAssets = shortShares * avgCost;
      const shortLiability = shortShares * bodPrice;
      return sum + shortAssets - shortLiability;
    }

    return sum;
  }, 0);
}

/**
 * Analyst long-only trade bump: max(0, buyLongNotional - sellLongNotional).
 * Ignores short open/cover.
 */
export function computeLongTradeNavAdjustment(tdgRows) {
  if (!Array.isArray(tdgRows) || tdgRows.length === 0) {
    return 0;
  }

  const netLongNotional = tdgRows.reduce((sum, trade) => {
    const isBuyLong =
      Number(trade?.action) === 0 && Number(trade?.position) === 0;
    const isSellLong =
      Number(trade?.action) === 3 && Number(trade?.position) === 0;
    if (!isBuyLong && !isSellLong) {
      return sum;
    }

    const completedShares = Math.abs(safeParseFloat(trade?.completedShares));
    const notional = safeParseFloat(
      trade?.totalCost,
      completedShares * safeParseFloat(trade?.fillPrice)
    );

    if (isBuyLong) {
      return sum + notional;
    }
    return sum - notional;
  }, 0);

  return Math.max(0, netLongNotional);
}

/** Strategy capital base ≈ BoD (yest) + max(0, long buy − long sell). */
export function computeCalculatedNav(rows, tdgRows = []) {
  return (
    computeSodNavFromRows(rows) + computeLongTradeNavAdjustment(tdgRows)
  );
}

function getTradeClassification(trade) {
  const isBuyAction = Number(trade.action) === 0;
  const isSellAction = Number(trade.action) === 3;
  const isLongPosition = Number(trade.position) === 0;
  const isShortPosition = Number(trade.position) === 1;

  let tradeType = "";
  if (isBuyAction && isLongPosition) {
    tradeType = "buylong";
  } else if (isSellAction && isShortPosition) {
    tradeType = "sellshort";
  } else if (isSellAction && isLongPosition) {
    tradeType = "selllong";
  } else if (isBuyAction && isShortPosition) {
    tradeType = "covershort";
  }

  return {
    isBuyAction,
    isSellAction,
    isLongPosition,
    isShortPosition,
    tradeType,
    isOpeningTrade: tradeType == "buylong" || tradeType == "sellshort",
    isClosingTrade: tradeType == "selllong" || tradeType == "covershort",
  };
}


function applyTradesToPosition(trades, initialShareCount, initialCost, initialPL) {
  const avgCostWithoutTrades =
    Math.abs(initialCost) / Math.abs(initialShareCount) || 0;

  let runningShareCount = safeParseFloat(initialShareCount);
  let runningCost = safeParseFloat(initialCost);
  let runningPL = safeParseFloat(initialPL);
  let totalTdg = 0;
  let hadOpening = false;
  let lastMarketPrice = null;

  // Apply closes before opens so cost basis for closes uses pre-trade avg
  const orderedTrades = [...trades].sort((a, b) => {
    const aClose = getTradeClassification(a).isClosingTrade ? 0 : 1;
    const bClose = getTradeClassification(b).isClosingTrade ? 0 : 1;
    return aClose - bClose;
  });

  for (const inTrade of orderedTrades) {
    const {
      isBuyAction,
      isSellAction,
      tradeType,
      isLongPosition,
      isOpeningTrade,
      isClosingTrade,
    } = getTradeClassification(inTrade);

    const completedShares = Math.abs(safeParseFloat(inTrade.completedShares));
    const tradeCost = safeParseFloat(
      inTrade.totalCost,
      completedShares * safeParseFloat(inTrade.fillPrice)
    );
    lastMarketPrice = safeParseFloat(inTrade.currentMarketPrice);
    const yestPrice = safeParseFloat(inTrade.yestPrice);
    const markNotional = lastMarketPrice * completedShares;
    const yestNotional = yestPrice * completedShares;

    const avgForClose =
      Math.abs(runningShareCount) > 0
        ? Math.abs(runningCost) / Math.abs(runningShareCount)
        : avgCostWithoutTrades;

    // tdgPnL = today's result on the trade (closes vs SOD; opens vs mark)
    // dayPlAdjust = keeps full-day P(L) correct given SOD×mark base (closes vs mark)
    let tdgPnL = 0;
    let dayPlAdjust = 0;
    if (isOpeningTrade) {
      hadOpening = true;
      tdgPnL = isLongPosition
        ? markNotional - tradeCost
        : tradeCost - markNotional;
      dayPlAdjust = tdgPnL;
    } else if (isClosingTrade) {
      tdgPnL = isLongPosition
        ? tradeCost - yestNotional
        : yestNotional - tradeCost;
      dayPlAdjust = isLongPosition
        ? tradeCost - markNotional
        : markNotional - tradeCost;
    }

    totalTdg += tdgPnL;

    switch (tradeType) {
      case "buylong":
        runningShareCount += completedShares;
        runningCost += tradeCost;
        break;
      case "sellshort":
        runningShareCount -= completedShares;
        runningCost -= tradeCost;
        break;
      case "selllong":
        runningShareCount -= completedShares;
        runningCost -= avgForClose * completedShares;
        break;
      case "covershort":
        runningShareCount += completedShares;
        runningCost += avgForClose * completedShares;
        break;
      default:
        // Fallback by action if position classification failed
        if (isBuyAction) {
          runningShareCount += completedShares;
          runningCost += tradeCost;
        } else if (isSellAction) {
          runningShareCount -= completedShares;
          runningCost -= tradeCost;
        }
        break;
    }

    runningPL += dayPlAdjust;
  }

  let avgCost = avgCostWithoutTrades;
  if (Math.abs(runningShareCount) > 0) {
    avgCost = hadOpening
      ? Math.abs(runningCost) / Math.abs(runningShareCount) || 0
      : avgCostWithoutTrades;
  }

  const result = {
    shareCount: runningShareCount,
    cost: runningCost,
    tdg: totalTdg,
    pL: runningPL,
    avgCost,
    avgCostWithoutTrades,
    marketPrice: lastMarketPrice,
  };

  return result;
}

// Global cache for expensive operations with timestamps
const priceCache = new Map();
const deltaCache = new Map();
const strategyCache = new Map();
const fundCache = new Map();

// Cache expiration time (3 minutes)
const CACHE_EXPIRY_TIME = 3 * 60 * 1000;

// Memoized price search function
function searchPrice(ticker, marketDataObject) {
  const lookupTicker =
    typeof ticker === "string" ? { symbol: ticker } : ticker || {};

  // Validate inputs
  if (
    !lookupTicker.symbol ||
    !marketDataObject ||
    !Array.isArray(marketDataObject)
  ) {
    return [0, 0, 0.9, "N/A", false, "N/A"];
  }

  const cacheKey = `${getLivePriceTickerKey(normalizeLivePriceEntry(lookupTicker))}-${marketDataObject.length}`;

  // Check if cache exists and is still valid
  try {
    if (priceCache.has(cacheKey)) {
      const cachedData = priceCache.get(cacheKey);
      if (cachedData && cachedData.timestamp && Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
        return cachedData.data;
      }
      // Remove expired cache entry
      priceCache.delete(cacheKey);
    }
  } catch (error) {
    console.warn("Error accessing price cache:", error);
    // Clear potentially corrupted cache entry
    priceCache.delete(cacheKey);
  }

  if (marketDataObject.length > 1) {
    let matchingTicker = findLivePriceForTicker(
      marketDataObject,
      lookupTicker
    );

    let c = matchingTicker?.price === undefined ? 0 : matchingTicker?.price;
    let l = matchingTicker?.yestPrice === undefined ? 0 : matchingTicker?.yestPrice;
    let d = matchingTicker?.expiration === undefined ? fetched : matchingTicker?.expiration;

    let e, f;
    try {
      d = formatTimeAgo(d);
      e = d?.msg || "N/A";
      f = d?.valid || false;
    } catch (error) {
      console.warn("Error in formatTimeAgo:", error);
      e = "N/A";
      f = false;
    }

    if (c === 0 || l === 0) {
      f = false;
    }

    // Cache delta lookup
    const deltaKey = getLivePriceTickerKey(normalizeLivePriceEntry(lookupTicker));
    let delta = 0.9;

    // Check if delta cache exists and is still valid
    if (deltaCache.has(deltaKey)) {
      const cachedData = deltaCache.get(deltaKey);
      if (Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
        delta = cachedData.data;
      } else {
        // Remove expired cache entry
        deltaCache.delete(deltaKey);
      }
    }

    if (!deltaCache.has(deltaKey)) {
      try {
        const deltaObject = fetchDeltasForTickers();
        if (Array.isArray(deltaObject)) {
          let matchingDelta = deltaObject.filter(
            (item) => item && Object.keys(item)[0] === deltaKey
          );
          delta = Math.abs(safeParseFloat(matchingDelta[0]?.[deltaKey], 0.9)) || 0.9;
        }
      } catch (error) {
        console.warn("Error fetching deltas:", error);
        delta = 0.9; // Default delta value
      }
      deltaCache.set(deltaKey, { data: delta, timestamp: Date.now() });
    }

    const result = [c, l, delta, e, f, matchingTicker?.market];
    try {
      priceCache.set(cacheKey, { data: result, timestamp: Date.now() });
    } catch (error) {
      console.warn("Error setting price cache:", error);
    }
    return result;
  } else {
    return [0, 0, 0.9, "N/A", false, "N/A"];
  }
}

// Memoized strategy matching
function getMatchingStrategies(selectedStrategy, selectedSide, strategies) {
  const cacheKey = `${selectedStrategy}-${selectedSide}-${strategies?.length}`;

  // Check if cache exists and is still valid
  if (strategyCache.has(cacheKey)) {
    const cachedData = strategyCache.get(cacheKey);
    if (Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
      return cachedData.data;
    }
    // Remove expired cache entry
    strategyCache.delete(cacheKey);
  }

  let matchingStrategies = [];

  if (selectedSide === "Both" || selectedSide === undefined) {
    matchingStrategies = strategies?.filter(
      (strategy) =>
        strategy.name === selectedStrategy ||
        strategy.name.startsWith(`${selectedStrategy}_`)
    );
  } else if (selectedSide === "Long" || selectedSide === "Short") {
    matchingStrategies = strategies?.filter((strategy) =>
      strategy.name.includes(`${selectedStrategy}_${selectedSide}`)
    );
  }

  strategyCache.set(cacheKey, { data: matchingStrategies, timestamp: Date.now() });
  return matchingStrategies;
}

// Memoized fund AUM calculation
function getFundAUM(selectedFund, selectedStrategy, accountingData, strategies) {
  const cacheKey = `${selectedFund}-${selectedStrategy}-${accountingData?.length}`;

  // Check if cache exists and is still valid
  if (fundCache.has(cacheKey)) {
    const cachedData = fundCache.get(cacheKey);
    if (Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
      return cachedData.data;
    }
    // Remove expired cache entry
    fundCache.delete(cacheKey);
  }

  let totalAUM = 10;

  if (accountingData?.length > 0) {
    if (selectedFund) {
      let matchingObj = accountingData[0].funds.find(
        (obj) => obj.fund == selectedFund
      );

      if (matchingObj) {
        if (
          selectedStrategy !== undefined &&
          selectedStrategy !== "ALL" &&
          selectedStrategy !== ""
        ) {
          const matchingStrategies = getMatchingStrategies(selectedStrategy, "Both", strategies);
          const strategyAUMs = matchingObj?.strategyAUMs || {};

          totalAUM = matchingStrategies.reduce((sum, strategy) => {
            const strategyId = strategy.strategyId;
            if (isIgnoredMonitorStrategy(strategyId)) {
              return sum;
            }
            return sum + (strategyAUMs[strategyId] || 0);
          }, 0);
        } else {
          totalAUM = matchingObj?.aum;
        }
      } else {
        totalAUM = accountingData[0]?.funds.reduce(
          (total, obj) => total + obj.aum,
          0
        );
      }
    } else {
      totalAUM = accountingData[0]?.funds.reduce(
        (total, obj) => total + obj.aum,
        0
      );
    }
  }

  fundCache.set(cacheKey, { data: totalAUM, timestamp: Date.now() });
  return totalAUM;
}

const MonitorScreenCalculation = ({
  selectedFund,
  selectedStrategy,
  selectedSide,
  lots,
  accountingData,
  funds,
  strategies,
  marketData,
  trades,
  isStrategist,
  markets,
  filled = true,
  useCalculatedNav = false
}) => {

  // Early return if no lots data
  if (!lots || lots.length === 0) {
    return [];
  }

  // Filter lots once at the beginning
  const filteredLots =
    lots?.filter(
      (lot) =>
        (lot?.ticker?.investment == 4 || lot?.ticker?.investment == 9) &&
        !isIgnoredMonitorStrategy(lot?.strategy)
    ) || [];

  // Early return if no filtered lots
  if (filteredLots.length === 0) {
    return [];
  }

  const valuesForTable = [];
  var visitedTicker = [];

  // Get matching strategies efficiently
  const matchingStrategiesRaw = isStrategist
    ? strategies?.filter(
      (strategy) =>
        strategy?.name === selectedStrategy ||
        strategy?.name?.startsWith(`${selectedStrategy}_`)
    ) || []
    : getMatchingStrategies(selectedStrategy, selectedSide, strategies);
  const matchingStrategies = (matchingStrategiesRaw || []).filter(
    (strategy) => !isIgnoredMonitorStrategy(strategy?.strategyId)
  );

  if (
    selectedStrategy &&
    matchingStrategiesRaw?.length > 0 &&
    matchingStrategies.length === 0
  ) {
    return [];
  }

  const accountingAUM = isStrategist
    ? (matchingStrategies.length > 0 ? matchingStrategies[0]?.aum : 10)
    : getFundAUM(selectedFund, selectedStrategy, accountingData, strategies);

  // Filter lots by fund and strategy once
  let processedLots = filteredLots;

  if (selectedFund) {
    processedLots = processedLots?.filter((obj) => {
      return obj?.fund == selectedFund;
    }) || [];
  }

  if (selectedStrategy) {
    const matchingStrategyIds = matchingStrategies?.map(
      (strategy) => strategy?.strategyId
    ) || [];

    processedLots = processedLots?.filter((obj) => matchingStrategyIds?.includes(obj?.strategy)) || [];
  }

  // Early return if no processed lots
  if (processedLots.length === 0) {
    return [];
  }

  // Pre-calculate trades data
  let calculatedData = [];
  if (trades && Array.isArray(trades)) {
    try {
      if (matchingStrategies.length > 0) {
        const str_id = matchingStrategies[0]?.strategyId;
        calculatedData = calculateTdg(trades, marketData, selectedFund, str_id, filled);
      } else {
        calculatedData = calculateTdg(trades, marketData, selectedFund, undefined, filled);
      }
    } catch (error) {
      console.warn("Error calculating TDG:", error);
      calculatedData = [];
    }
  }

  // Create a Map for faster trade lookups (symbol → one or more calculateTdg rows)
  const tradeMap = new Map();
  calculatedData?.forEach((item) => {
    if (!item?.symbol) return;
    const key = item.symbol.toLowerCase();
    if (!tradeMap.has(key)) {
      tradeMap.set(key, []);
    }
    tradeMap.get(key).push(item);
  });

  // Process lots more efficiently with optimized loops
  for (var i = 0; i < processedLots?.length; i++) {
    const currentLot = processedLots[i];
    if (!currentLot || !currentLot.ticker || !currentLot.ticker.symbol) continue;

    if (!visitedTicker.includes(currentLot.ticker.symbol)) {
      if (currentLot.shareCount == 0) continue;

      visitedTicker.push(currentLot.ticker.symbol);

      let livePrices = searchPrice(currentLot.ticker, marketData);
      let tableFund = selectedFund || "All";
      let tableStrategyId = selectedStrategy ? currentLot.strategy : "All";
      let tableStrategyName = "All";
      if (selectedStrategy && currentLot.strategy) {
        try {
          tableStrategyName = findName(currentLot.strategy, strategies) || "All";
        } catch (error) {
          console.warn("Error finding strategy name:", error);
          tableStrategyName = "All";
        }
      }

      let marketName = "";
      const matchedMarket = markets?.find(
        (market) => market?.marketId == livePrices[5]
      );

      if (matchedMarket) {
        marketName = matchedMarket?.name || "";
      }
      if (marketName == "FMP") {
        marketName = "Primary"
      }

      var valuesForTableObject = {
        tickerSymbol: currentLot.ticker.stock
          ? currentLot.ticker.symbol + "/" + currentLot.ticker.stock
          : currentLot.ticker.symbol,
        tickerInvestment: currentLot.ticker.investment || 0,
        sharePrice: livePrices[0],
        last: livePrices[1],
        delta: livePrices[2],
        size: 0,
        equityShareCount: 0,
        deltaAdjustedShareCount: 0,
        impactOfOptionsContracts: 0,
        ch: 0,
        pL: 0,
        fund: tableFund,
        fundStrategyName: tableStrategyName,
        strategyName: tableStrategyId,
        dataSource: marketName,
        fetched: livePrices[3],
        valid: livePrices[4],
        fundName: "",
        custodian: "",
        shareCount: 0,
        Strategy: [],
        chPercetage: 0,
        profitLossWeek: 0,
        profitLossMonth: 0,
        profitLossQuarter: 0,
        profitLossYear: 0,
        cost: 0,
        tdg: 0,
      };

      // Optimized loop through all lots for the same ticker
      for (var j = 0; j < processedLots.length; j++) {
        const innerLot = processedLots[j];
        if (!innerLot || !innerLot.ticker || !innerLot.ticker.symbol) continue;

        if (innerLot.ticker.symbol === currentLot.ticker.symbol) {
          if (innerLot.ticker.investment === 10) {
            valuesForTableObject.impactOfOptionsContracts +=
              (innerLot.shareCount || 0) * contractUnits * valuesForTableObject.delta;
          }
          if (innerLot.ticker.investment === 4 || innerLot.ticker.investment === 9) {
            valuesForTableObject.equityShareCount += innerLot.shareCount || 0;
            valuesForTableObject.shareCount += innerLot.shareCount || 0;
            valuesForTableObject.cost += innerLot.cost || 0;
          }

          valuesForTableObject.profitLossWeek += innerLot.profitLossWeek || 0;
          valuesForTableObject.profitLossMonth += innerLot.profitLossMonth || 0;
          valuesForTableObject.profitLossQuarter += innerLot.profitLossQuarter || 0;
          valuesForTableObject.profitLossYear += innerLot.profitLossYear || 0;
          valuesForTableObject.custodian = innerLot?.account?.custodian || "";
        }
      }

      valuesForTableObject.deltaAdjustedShareCount =
        calcualteDeltaAdjustedShareCount(
          valuesForTableObject.equityShareCount,
          valuesForTableObject.impactOfOptionsContracts
        );

      valuesForTableObject.ch = calculateCh(
        valuesForTableObject.sharePrice,
        valuesForTableObject.last
      );

      valuesForTableObject.pL = safeToFixed(
        caluclatePL(
          valuesForTableObject.ch,
          valuesForTableObject.equityShareCount
        ),
        1
      );

      const chPercentRaw = caluclateCHPercentage(
        valuesForTableObject.sharePrice,
        valuesForTableObject.last
      );
      valuesForTableObject.chPercetage = chPercentRaw;

      let matchingFund = funds?.find(
        (obj) => obj?.fundId === valuesForTableObject.fund
      );
      if (matchingFund) {
        valuesForTableObject.fundName = matchingFund?.abbreviation || "ALL";
      } else {
        valuesForTableObject.fundName = "ALL";
      }

      const inTrades =
        tradeMap.get(currentLot.ticker.symbol.toLowerCase()) || [];

      valuesForTableObject.avgCostWithoutTrades =
        Math.abs(valuesForTableObject.cost) /
        Math.abs(valuesForTableObject.shareCount) || 0;
      valuesForTableObject.deltaAdjustedShareCountWithoutTrades =
        valuesForTableObject.deltaAdjustedShareCount;

      if (inTrades.length > 0) {
        valuesForTableObject.pLWithoutTrades = valuesForTableObject.pL;
        valuesForTableObject.sizeWithoutTrades = valuesForTableObject.size;

        const applied = applyTradesToPosition(
          inTrades,
          valuesForTableObject.shareCount,
          valuesForTableObject.cost,
          valuesForTableObject.pL
        );

        valuesForTableObject.deltaAdjustedShareCount = applied.shareCount;
        valuesForTableObject.tdg = safeParseFloat(applied.tdg);
        valuesForTableObject.pL = safeParseFloat(applied.pL).toFixed(1);
        valuesForTableObject.avgCost = applied.avgCost;
        valuesForTableObject.sharePrice =
          applied.marketPrice ?? valuesForTableObject.sharePrice;
      } else {
        valuesForTableObject.avgCost = valuesForTableObject.avgCostWithoutTrades;
      }

      valuesForTable.push(valuesForTableObject);
    }
  }

  // New symbols: group calculateTdg rows by symbol into one table row each
  if (trades && Array.isArray(calculatedData)) {
    const symbolsWithLots = new Set(
      visitedTicker.map((symbol) => symbol.toLowerCase())
    );

    const newTradeGroups = new Map();
    calculatedData.forEach((item) => {
      if (!item?.symbol) return;
      const key = item.symbol.toLowerCase();
      if (symbolsWithLots.has(key)) return;
      if (!newTradeGroups.has(key)) {
        newTradeGroups.set(key, []);
      }
      newTradeGroups.get(key).push(item);
    });

    newTradeGroups.forEach((groupTrades) => {
      const item = groupTrades[0];
      const applied = applyTradesToPosition(groupTrades, 0, 0, 0);

      const priceChange = calculateCh(
        item.currentMarketPrice,
        safeParseFloat(item.yestPrice)
      );

      const chPercentRawNewTrade = caluclateCHPercentage(
        item.currentMarketPrice,
        safeParseFloat(item.yestPrice)
      );

      let tableFund = selectedFund || "All";
      let tableStrategyId = "All";
      let tableStrategyName = "All";
      if (selectedStrategy && matchingStrategies?.length > 0) {
        tableStrategyId = matchingStrategies[0]?.strategyId || "All";
        try {
          tableStrategyName =
            findName(tableStrategyId, strategies) ||
            matchingStrategies[0]?.name ||
            "All";
        } catch (error) {
          console.warn("Error finding strategy name for new trade:", error);
          tableStrategyName = matchingStrategies[0]?.name || "All";
        }
      }

      let tableFundName = "ALL";
      if (selectedFund) {
        const matchingFund = funds?.find(
          (obj) => obj?.fundId === selectedFund
        );
        tableFundName = matchingFund?.abbreviation || "ALL";
      }

      valuesForTable.push({
        size: "0.00",
        sharePrice: item.currentMarketPrice,
        deltaAdjustedShareCount: applied.shareCount,
        deltaAdjustedShareCountWithoutTrades: 0,
        avgCostWithoutTrades: 0,
        ch: priceChange,
        pL: safeToFixed(applied.pL),
        avgCost: applied.avgCost,
        last: safeParseFloat(item.yestPrice),
        tickerSymbol: item.symbol,
        chPercetage: chPercentRawNewTrade,
        fund: tableFund,
        fundStrategyName: tableStrategyName,
        strategyName: tableStrategyId,
        fundName: tableFundName,
        fetched: item.fetched,
        valid: item.valid,
        tdg: safeToFixed(applied.tdg),
        dataSource: item.dataSource,
      });
    });
  }

  const calculatedNav = computeCalculatedNav(valuesForTable, calculatedData);
  valuesForTable.calculatedNav = calculatedNav;
  const calculatedNavDenominator = calculatedNav > 0 ? calculatedNav : 10;
  const accountingDenominator =
    safeParseFloat(accountingAUM) > 0 ? safeParseFloat(accountingAUM) : 10;
  const sizeDenominator = useCalculatedNav
    ? calculatedNavDenominator
    : accountingDenominator;

  valuesForTable.forEach((row) => {
    row.size = safeToFixed(
      calculateSize(
        Math.abs(safeParseFloat(row.deltaAdjustedShareCount)),
        safeParseFloat(row.sharePrice),
        sizeDenominator
      ),
      2
    );

    if (row.deltaAdjustedShareCountWithoutTrades !== undefined) {
      row.sizeWithoutTrades = safeToFixed(
        calculateSize(
          Math.abs(safeParseFloat(row.deltaAdjustedShareCountWithoutTrades)),
          safeParseFloat(row.sharePrice),
          sizeDenominator
        ),
        2
      );
    }
  });

  return valuesForTable;
};

export default MonitorScreenCalculation;
